<template>
    <article class="tk-insight-document">
        <div class="tk-insight-document__meta">
            <span>Published {{ formatDateTime(insight.published_at) }}</span>
            <span v-if="insight.author_name">by {{ insight.author_name }}</span>
            <span>{{ insight.content.source_coverage }}</span>
        </div>

        <div class="tk-insight-document__view-toggle">
            <q-btn
                v-if="insight.status === 'published'"
                outline
                no-caps
                color="primary"
                icon="download"
                label="Download Word document"
                :loading="downloading"
                :disable="downloading"
                data-test="download-insight"
                @click="downloadInsight"
            />
            <q-toggle v-model="showRaw" dense color="primary" label="View raw" />
        </div>

        <q-input
            v-if="showRaw"
            class="tk-insight-document__raw"
            data-test="raw-insight"
            :model-value="rawInsight"
            type="textarea"
            outlined
            readonly
            autogrow
        />

        <template v-else>
            <section class="tk-insight-document__section tk-insight-document__section--overview">
                <div class="tk-insight-document__label"><h3>Overview</h3></div>
                <p class="tk-insight-document__narrative">{{ narrativeWithoutCitationMarkers(insight.content.overview) }}</p>
            </section>

            <section
                v-for="section in insight.content.sections"
                :key="section.name"
                :class="['tk-insight-document__section', { 'tk-insight-document__section--suggested': isSuggested(section.name) }]"
            >
                <div class="tk-insight-document__label">
                    <h3>
                        <q-icon v-if="isSuggested(section.name)" name="auto_awesome" size="17px" />
                        {{ sectionLabel(section.name) }}
                    </h3>
                </div>
                <p class="tk-insight-document__narrative">{{ narrativeWithoutCitationMarkers(section.narrative) }}</p>
            </section>
        </template>
    </article>
</template>

<script>
import api from "@/services/api/api";
import dayjs from "@/services/util/dayjs";
import { promptDownload } from "@/services/util/file";
import notify from "@/services/util/notify";
import { INSIGHT_SECTION_DEFINITIONS, narrativeWithoutCitationMarkers } from "@/services/util/engagementInsights";

export default {
    name: "TkEngagementInsightDocument",
    props: {
        personId: {
            type: Number,
            required: true,
        },
        engagementId: {
            type: Number,
            required: true,
        },
        insight: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            showRaw: false,
            downloading: false,
        };
    },
    computed: {
        rawInsight() {
            const overview = `Overview\n${narrativeWithoutCitationMarkers(this.insight.content.overview)}`;
            const sections = this.insight.content.sections.map(section => (
                `${this.sectionLabel(section.name)}\n${narrativeWithoutCitationMarkers(section.narrative)}`
            ));
            return [overview, ...sections].join("\n\n");
        },
    },
    methods: {
        async downloadInsight() {
            if (this.downloading || this.insight.status !== "published") return;
            const insightId = this.insight.id;
            this.downloading = true;
            try {
                const response = await api.tkPeople.downloadEngagementInsight(this.personId, this.engagementId, insightId);
                promptDownload(response.data, `engagement-insight-${insightId}.docx`);
            } catch (error) {
                const statusText = error?.response?.statusText;
                notify.error(
                    statusText && statusText !== "Internal Server Error"
                        ? statusText
                        : "Unable to download the engagement insight. Please try again.",
                    "top"
                );
            } finally {
                this.downloading = false;
            }
        },
        narrativeWithoutCitationMarkers,
        formatDateTime(value) {
            return value ? dayjs(value).format("D MMM YYYY, h:mm A") : "";
        },
        sectionDefinition(name) {
            return INSIGHT_SECTION_DEFINITIONS.find(definition => definition.name === name);
        },
        sectionLabel(name) {
            return this.sectionDefinition(name)?.label || name;
        },
        isSuggested(name) {
            return !!this.sectionDefinition(name)?.suggested;
        },
    },
};
</script>

<style scoped>
.tk-insight-document {
    padding: 6px 24px 28px;
}

.tk-insight-document__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 5px 14px;
    padding: 12px 0 18px;
    border-bottom: 1px solid #dfe4e0;
    color: #68756f;
    font-size: 0.8rem;
}

.tk-insight-document__view-toggle {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    justify-content: flex-end;
    padding-top: 12px;
}

.tk-insight-document__raw {
    margin-top: 12px;
}

.tk-insight-document__raw :deep(.q-field__native) {
    color: #35453e;
    font-family: inherit;
    font-size: 0.96rem;
    line-height: 1.7;
}

.tk-insight-document__section {
    display: grid;
    grid-template-columns: 170px minmax(0, 1fr);
    gap: 22px;
    padding: 24px 0;
    border-bottom: 1px solid #e1e6e2;
}

.tk-insight-document__section--suggested {
    margin-top: 22px;
    padding: 22px 18px;
    border: 1px solid #eadbbf;
    border-radius: 8px;
    background: #fbf5e9;
}

.tk-insight-document__label h3 {
    margin: 2px 0 0;
    color: #45544d;
    font-size: 0.83rem;
    font-weight: 600;
    line-height: 1.4;
}

.tk-insight-document__section--suggested h3 {
    color: #80602f;
}

.tk-insight-document__narrative {
    margin: 0;
    color: #35453e;
    font-size: 0.96rem;
    line-height: 1.7;
    white-space: pre-wrap;
}

@media (max-width: 700px) {
    .tk-insight-document {
        padding: 4px 14px 24px;
    }

    .tk-insight-document__section {
        grid-template-columns: 1fr;
        gap: 12px;
    }

}
</style>
